import supabase from "../supabase/supabase";

const db = () => supabase.schema("accounting");

export const normalizeAccountType = (type) => {
  const value = String(type || "")
    .trim()
    .toLowerCase();
  if (["expense", "expenses"].includes(value)) return "Expense";
  if (["income", "revenue", "revenues"].includes(value)) return "Income";
  if (
    [
      "asset",
      "assets",
      "bank",
      "cash",
      "cash & bank",
      "cash and bank",
      "checking",
      "savings",
      "cash and cash equivalents",
    ].includes(value)
  )
    return "Asset";
  if (
    [
      "liability",
      "liabilities",
      "liabilities & credit cards",
      "liability & credit cards",
      "credit card",
      "credit cards",
      "credit card accounts",
      "creditcard",
      "creditcard accounts",
    ].includes(value)
  )
    return "Liability";
  if (["equity", "equities"].includes(value)) return "Equity";
  return String(type || "").trim();
};

export const ACCOUNT_NORMAL_SIDE = {
  Asset: "Debit",
  Expense: "Debit",
  Liability: "Credit",
  Equity: "Credit",
  Income: "Credit",
};

const requireOrganization = (id) => {
  if (!id) throw new Error("Select a business before managing its accounts.");
  return id;
};

const check = ({ data, error }) => {
  if (error) throw error;
  return data;
};

const cleanPayload = (input) => {
  const type = normalizeAccountType(input.type);
  if (!ACCOUNT_NORMAL_SIDE[type])
    throw new Error("Select a valid account type.");
  const name = String(input.name || "").trim();
  const sub_type = String(input.sub_type || "").trim();
  const code = String(input.code || "").trim();
  const currency = String(input.currency || "")
    .trim()
    .toUpperCase();
  if (!name) throw new Error("Account name is required.");
  if (!sub_type) throw new Error("Account detail type is required.");
  if (!/^[A-Z]{3}$/.test(currency))
    throw new Error("Select a valid three-letter currency.");
  if (name.length > 160 || code.length > 40 || sub_type.length > 120)
    throw new Error("One or more account fields are too long.");
  return {
    name,
    type,
    sub_type,
    code,
    currency,
    description: String(input.description || "").trim(),
  };
};

const findDuplicate = async (organizationId, payload, excludeId = null) => {
  const accounts = await coaService.getChartOfAccounts(organizationId);
  const normalized = (value) =>
    String(value || "")
      .trim()
      .toLocaleLowerCase();
  const match = accounts.find(
    (account) =>
      account.id !== excludeId &&
      ((payload.code &&
        normalized(account.code) === normalized(payload.code)) ||
        normalized(account.name) === normalized(payload.name)),
  );
  if (match)
    throw new Error(
      payload.code && normalized(match.code) === normalized(payload.code)
        ? `Account code ${payload.code} is already used by ${match.name}.`
        : `An account named ${payload.name} already exists in this business.`,
    );
};

export const coaService = {
  // Kept for legacy callers. New write operations require an explicit organization ID.
  async getAccountId(explicitOrganizationId = null) {
    if (explicitOrganizationId) return explicitOrganizationId;
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    if (!user) return null;
    const memberships = check(
      await db()
        .from("organization_users")
        .select("organization_id")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(2),
    );
    // Do not silently select the first business when the user belongs to several.
    return memberships?.length === 1 ? memberships[0].organization_id : null;
  },

  async getChartOfAccounts(organizationId = null) {
    const org = organizationId || (await this.getAccountId());
    if (!org) return [];
    const rows = check(
      await db()
        .from("accounts")
        .select("*")
        .eq("organization_id", org)
        .order("code", { ascending: true })
        .order("name", { ascending: true }),
    );
    return (rows || []).map((row) => ({
      ...row,
      type: normalizeAccountType(row.type),
    }));
  },

  async getAccounts(organizationId = null) {
    return this.getChartOfAccounts(organizationId);
  },

  async getOrganizationCurrency(organizationId) {
    requireOrganization(organizationId);
    const organization = check(
      await db()
        .from("organizations")
        .select("currency")
        .eq("id", organizationId)
        .single(),
    );
    return String(organization.currency || "USD").toUpperCase();
  },

  async createAccount(accountData, organizationId) {
    const org = requireOrganization(organizationId);
    const payload = cleanPayload(accountData);
    await findDuplicate(org, payload);
    return check(
      await db()
        .from("accounts")
        .insert({ ...payload, organization_id: org, is_active: true })
        .select("*")
        .single(),
    );
  },

  async updateAccount(id, accountData, organizationId) {
    const org = requireOrganization(organizationId);
    if (!id) throw new Error("Account ID is required.");
    const existing = check(
      await db()
        .from("accounts")
        .select("*")
        .eq("id", id)
        .eq("organization_id", org)
        .single(),
    );
    if (existing.is_active === false)
      throw new Error("Restore this account before editing it.");
    const payload = cleanPayload(accountData);
    await findDuplicate(org, payload, id);

    const classificationChanged =
      normalizeAccountType(existing.type) !== payload.type;
    const currencyChanged =
      String(existing.currency || "USD").toUpperCase() !== payload.currency;
    if (classificationChanged || currencyChanged) {
      const [ledgerCount, bankLinks, invoiceLinks, importLinks] =
        await Promise.all([
          this.getAccountTransactionCount(id, org),
          check(
            await db()
              .from("bank_accounts")
              .select("id")
              .eq("organization_id", org)
              .eq("account_id", id)
              .limit(1),
          ),
          check(
            await db()
              .from("invoice_items")
              .select("id")
              .eq("organization_id", org)
              .eq("account_id", id)
              .limit(1),
          ),
          check(
            await db()
              .from("import_transactions")
              .select("id")
              .eq("organization_id", org)
              .eq("suggested_account_id", id)
              .limit(1),
          ),
        ]);
      if (
        ledgerCount ||
        bankLinks?.length ||
        invoiceLinks?.length ||
        importLinks?.length
      ) {
        throw new Error(
          "This account is already used by accounting records. Its account type and currency cannot be changed here. Create a new account instead.",
        );
      }
    }
    return check(
      await db()
        .from("accounts")
        .update(payload)
        .eq("id", id)
        .eq("organization_id", org)
        .eq("is_active", true)
        .select("*")
        .single(),
    );
  },

  async getAccountTransactionCount(id, organizationId) {
    const org = requireOrganization(organizationId);
    const { count, error } = await db()
      .from("transactions")
      .select("id", { count: "exact", head: true })
      .eq("organization_id", org)
      .eq("account_id", id);
    if (error) throw error;
    return count || 0;
  },

  async deleteAccount(id, organizationId) {
    const org = requireOrganization(organizationId);
    if (!id) throw new Error("Account ID is required.");
    const [transactionCount, bankLinks] = await Promise.all([
      this.getAccountTransactionCount(id, org),
      check(
        await db()
          .from("bank_accounts")
          .select("id")
          .eq("organization_id", org)
          .eq("account_id", id)
          .eq("is_active", true)
          .limit(1),
      ),
    ]);
    if (bankLinks?.length)
      throw new Error(
        "This account is linked to an active bank connection. Disconnect or reassign it before archiving.",
      );
    const data = check(
      await db()
        .from("accounts")
        .update({ is_active: false })
        .eq("id", id)
        .eq("organization_id", org)
        .select("id, is_active")
        .single(),
    );
    return { ...data, transactionCount };
  },

  async restoreAccount(id, organizationId) {
    const org = requireOrganization(organizationId);
    if (!id) throw new Error("Account ID is required.");
    return check(
      await db()
        .from("accounts")
        .update({ is_active: true })
        .eq("id", id)
        .eq("organization_id", org)
        .select("id, is_active")
        .single(),
    );
  },

  async getOrCreateUncategorizedAccount(organizationId) {
    const org = requireOrganization(organizationId);
    const accounts = await this.getChartOfAccounts(org);
    const found = accounts.find(
      (a) =>
        a.type === "Expense" &&
        a.name.trim().toLowerCase() === "uncategorized expense",
    );
    if (found) {
      if (!found.is_active)
        throw new Error(
          "Uncategorized Expense is archived. Restore it before importing transactions.",
        );
      return found;
    }
    const currency = await this.getOrganizationCurrency(org);
    const code = accounts.some((a) => a.code === "9999") ? "" : "9999";
    return this.createAccount(
      {
        name: "Uncategorized Expense",
        type: "Expense",
        sub_type: "Other Expense",
        code,
        currency,
      },
      org,
    );
  },

  async getOrCreateUncategorizedIncomeAccount(organizationId) {
    const org = requireOrganization(organizationId);
    const accounts = await this.getChartOfAccounts(org);
    const found = accounts.find(
      (a) =>
        a.type === "Income" &&
        a.name.trim().toLowerCase() === "uncategorized income",
    );
    if (found) {
      if (!found.is_active)
        throw new Error(
          "Uncategorized Income is archived. Restore it before importing transactions.",
        );
      return found;
    }
    const currency = await this.getOrganizationCurrency(org);
    const code = accounts.some((a) => a.code === "8999") ? "" : "8999";
    return this.createAccount(
      {
        name: "Uncategorized Income",
        type: "Income",
        sub_type: "Other Income",
        code,
        currency,
      },
      org,
    );
  },
};